'use client'

import { CheckoutSuccessNotification } from '@/components/Checkout/CheckoutSuccessNotification'
import { AgentModeSelector } from '@/components/Dashboard/AgentModeSelector'
import EmptyState from '@/components/Dashboard/EmptyState'
import GetStartedCards from '@/components/Dashboard/GetStartedCards'
import SessionTemplates from '@/components/Dashboard/SessionTemplates'
import DashboardScheduledTasks from '@/components/DashboardV2/DashboardScheduledTasks'
import FavoriteAutomations from '@/components/DashboardV2/FavoriteAutomations'
import LandingInput from '@/components/Landing/LandingInput'
import PricingModal from '@/components/Pricing/PricingModal'
import { useTheme } from '@/components/Theme/ThemeProvider'
import { OutageStatusBanner } from '@/components/ui/OutageStatusBanner'
import { Spinner } from '@/components/ui/spinner'
import { useOnboardingCheck } from '@/hooks/useOnboardingCheck'
import { usePricingModal } from '@/hooks/usePricingModal'
import { analytics } from '@/lib/analytics'
import { getThemeForAgent } from '@/lib/features/animatedBackground/agentThemes'
import { useGetAllUserSessionsQuery } from '@/lib/features/session/sessionApi'
import {
  useSessionAwareOnboardingQuery,
  useSessionAwareUserQuery,
  useSessionAwareUserQuotaQuery,
} from '@/lib/features/user/userApi'
import { useGetWorkspaceWorkflowMetadataQuery } from '@/lib/features/workflow/workflowApi'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { AgentType } from 'sprites-types/api/v2/copilot'
import Session from 'supertokens-web-js/recipe/session'

export default function DashboardClient() {
  const router = useRouter()
  const [hasSession, setHasSession] = useState<boolean | null>(null)
  const [selectedAgentMode, setSelectedAgentMode] = useState<AgentType>(AgentType.META_ADS)
  const { setTheme } = useTheme()
  const { data: userData, isLoading: userLoading } = useSessionAwareUserQuery()
  const { data: quota } = useSessionAwareUserQuotaQuery()
  const { needsOnboarding } = useOnboardingCheck()
  const { setOpen: openPricingModal } = usePricingModal()
  const { data: onboardingData } = useSessionAwareOnboardingQuery()
  const hasTrackedFirstLoad = useRef(false)
  const user = userData?.user
  const workspaceId = userData?.workspace?.id

  // Track first dashboard load after onboarding
  useEffect(() => {
    if (
      onboardingData?.onboardingCompleted &&
      !hasTrackedFirstLoad.current &&
      hasSession !== null
    ) {
      hasTrackedFirstLoad.current = true
      analytics.onboarding.postOnboardingAction('dashboard_first_load', '/dashboard')
    }
  }, [onboardingData, hasSession])

  // Set initial theme on mount
  useEffect(() => {
    const theme = getThemeForAgent(selectedAgentMode)
    setTheme(theme)
  }, [selectedAgentMode, setTheme])

  // Update theme when agent mode changes
  const handleAgentModeChange = (mode: AgentType) => {
    setSelectedAgentMode(mode)
    const theme = getThemeForAgent(mode)
    setTheme(theme)
  }

  // Check session on mount and redirect if no session
  useEffect(() => {
    const checkSession = async () => {
      const sessionExists = await Session.doesSessionExist()
      if (!sessionExists) {
        // If there's already a redirect param, preserve it; otherwise use current pathname
        const searchParams = new URLSearchParams(window.location.search)
        const existingRedirect = searchParams.get('redirect')
        const redirectTarget = existingRedirect || window.location.pathname
        router.push(`/?signup=1&redirect=${encodeURIComponent(redirectTarget)}`)
        return
      }
      setHasSession(sessionExists)
    }
    checkSession()
  }, [router])

  // Light paywall: Show pricing modal if user is on free plan with 0 credits
  useEffect(() => {
    if (quota?.subscription?.name === 'Free' && quota?.credits === 0) {
      openPricingModal()
    }
  }, [quota, openPricingModal])

  // Check for sessions and workflows
  const {
    data: sessionData,
    isLoading: sessionsLoading,
    refetch: refetchSessions,
  } = useGetAllUserSessionsQuery(
    { limit: 1, offset: 0, includeInteractions: false },
    {
      skip: !workspaceId,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  )
  const {
    data: workflowData,
    isLoading: workflowsLoading,
    refetch: refetchWorkflows,
  } = useGetWorkspaceWorkflowMetadataQuery(
    { limit: 1, offset: 0 },
    {
      skip: !workspaceId,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  )
  const {
    data: bookmarkedWorkflowData,
    isLoading: bookmarkedWorkflowsLoading,
    refetch: refetchBookmarkedWorkflows,
  } = useGetWorkspaceWorkflowMetadataQuery(
    { limit: 1, offset: 0, bookmarked: true },
    {
      skip: !workspaceId,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  )

  const hasSessions = (sessionData?.sessions?.length || 0) > 0
  const hasWorkflows = (workflowData?.workflows?.length || 0) > 0
  const hasFavorites = (bookmarkedWorkflowData?.workflows?.length || 0) > 0

  // Show loading if:
  // 1. User data is loading (need workspaceId)
  // 2. Queries are actively loading
  // 3. We have workspaceId but haven't received data yet (queries haven't completed first fetch)
  const isLoadingData =
    userLoading ||
    sessionsLoading ||
    workflowsLoading ||
    bookmarkedWorkflowsLoading ||
    (!!workspaceId &&
      sessionData === undefined &&
      workflowData === undefined &&
      bookmarkedWorkflowData === undefined)

  // Refetch data when component mounts to ensure we have latest data
  useEffect(() => {
    if (workspaceId) {
      refetchSessions()
      refetchWorkflows()
      refetchBookmarkedWorkflows()
    }
  }, [workspaceId, refetchSessions, refetchWorkflows, refetchBookmarkedWorkflows])

  // Show loading while checking session
  if (hasSession === null) {
    return null
  }

  // Redirect to onboarding if needed (handled by useOnboardingCheck hook)
  if (needsOnboarding) {
    return null
  }

  const titleText = userLoading
    ? 'What should we get done?'
    : `What should we get done, ${user?.name || ''}?`

  const [favoritesOrder, sessionsOrder] = hasFavorites
    ? ['order-1', 'order-2']
    : ['order-2', 'order-1']

  return (
    <>
      <CheckoutSuccessNotification />
      <PricingModal />
      <OutageStatusBanner />
      <AgentModeSelector selectedMode={selectedAgentMode} onModeChange={handleAgentModeChange} />
      <div className="mb-16 flex flex-col items-center">
        <h1 className="sr-only">Dashboard</h1>
        <LandingInput
          titleText={titleText}
          isFixed={false}
          isScrolled={false}
          agentType={selectedAgentMode}
        />
      </div>

      {/* Get Started Cards - shown right after input. Disabled for now */}
      {!isLoadingData && <GetStartedCards />}

      <DashboardScheduledTasks />

      {/* Show either Getting Started OR regular sections */}
      {isLoadingData ? (
        <div className="flex min-h-[400px] items-center justify-center">
          <Spinner size="lg" variant="simple" />
        </div>
      ) : !hasSessions && !hasWorkflows ? (
        <EmptyState hasSessions={hasSessions} hasWorkflows={hasWorkflows} />
      ) : (
        <div className="flex flex-col">
          <div className={favoritesOrder}>
            <FavoriteAutomations />
          </div>
          <div className={sessionsOrder}>
            <SessionTemplates />
          </div>
        </div>
      )}
    </>
  )
}
